import { useCallback, type FC } from "react";
import styled from "styled-components";

import { useDeepRootSelector, usePagination, useRootDispatch, useRootSelector, useTableDataSorting } from "@ds/hooks";

import { DEFAULT_TABLE_TAB } from "@ds/modules/table-data/redux/slice";
import { MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { DndDropzone } from "@ds/components/dnd-dropzone";
import { Loading } from "@ds/components/loaders/loading";
import { TableWithPagination } from "@ds/components/table/main-table";
import { Column, getEntityIdColumn, getSelectionColumn } from "@ds/components/table/table-columns";
import { TotalSelectedLabel } from "@ds/components/table/total-selected-label";
import { formatBytes, formatChannels, formatDuration, formatSampleRate } from "@ds/utils/localization/assets";

import { selectAssetsIsLoading, selectSelectedAssets, selectTableAssets } from "../redux/selectors/common-selectors";
import { AssetsRemoteOperation, assetsActions } from "../redux/slice";
import { createAssetFiltersByTableType, getSupportedFormatsByTableType } from "../utils/helpers";
import { AssetThumbnail } from "./common/asset-thumbnail";

const TableWrapper = styled.div`
  height: 100%;
  .p-datatable-tbody tr {
    height: 150px;

    &.p-datatable-emptymessage {
      height: initial;
    }
  }
`;

const getPreviewBodyTemplate = (asset: Asset) => <AssetThumbnail asset={asset} />;
const getDescriptionBodyTemplate = (asset: Asset) => (
  <div className="text-sm">
    <div className="flex align-items-center">
      <i className="pi pi-paperclip mr-2"></i>
      <div className="mt-1">
        <div className="font-medium">{asset.name}</div>
        <div className="text-xs font-italic">{asset.content_type}</div>
      </div>
    </div>
    <div className="mt-5 flex align-items-center gap-3">
      <i className="pi pi-info-circle"></i>
      <span>{formatBytes(asset.file_size)}</span>
      {asset.media_info?.duration && <span>{formatDuration(asset.media_info.duration)}</span>}
      {asset.media_info?.width && asset.media_info?.height && (
        <span>
          {asset.media_info.width} × {asset.media_info.height}
        </span>
      )}
      {asset.media_info?.sample_rate && <span>{formatSampleRate(asset.media_info.sample_rate)}</span>}
      {asset.media_info?.channels && <span>{formatChannels(asset.media_info.channels)}</span>}
    </div>
  </div>
);

export const AssetsTable: FC<{ tableType: MainTableDataTypeEnum; onUpload: (files: File[]) => void }> = ({
  tableType,
  onUpload,
}) => {
  const [selectedEntities, entities] = useDeepRootSelector(state => [
    selectSelectedAssets(state, tableType),
    selectTableAssets(state, tableType),
  ]);

  const isLoading = useRootSelector(selectAssetsIsLoading);
  const checkboxIsLoading = useRootSelector(state => selectAssetsIsLoading(state, AssetsRemoteOperation.CHECKBOXES));

  const queryFilters = createAssetFiltersByTableType({ tableType });
  const [pagination, setPagination] = usePagination(tableType, DEFAULT_TABLE_TAB, queryFilters);
  const [{ sortOrder, sortField }, setSorting] = useTableDataSorting(tableType, DEFAULT_TABLE_TAB, queryFilters);

  const dispatch = useRootDispatch();
  const onToggleRowHandler = useCallback(
    (asset: Asset) => dispatch(assetsActions.toggleSelected(asset.id, tableType)),
    [dispatch, tableType],
  );

  const onSingleRowSelectHandler = useCallback(
    (asset: Asset) => dispatch(assetsActions.selectAssets([asset], tableType)),
    [dispatch, tableType],
  );

  const onToggleAllHandler = useCallback(
    (isSelectedAll: boolean) => {
      if (isSelectedAll) {
        dispatch(assetsActions.fetchAssets(queryFilters, undefined, AssetsRemoteOperation.CHECKBOXES));
      } else {
        dispatch(assetsActions.selectAssets([], tableType));
      }
    },
    [dispatch, queryFilters, tableType],
  );

  return (
    <Loading isLoading={isLoading}>
      <DndDropzone acceptMediaTypes={getSupportedFormatsByTableType(tableType)} onUpload={onUpload}>
        <TableWrapper>
          <TableWithPagination
            value={entities}
            selection={selectedEntities}
            onSingleRowSelect={onSingleRowSelectHandler}
            toggleRowSelected={onToggleRowHandler}
            loading={isLoading}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={setSorting}
            pagination={pagination}
            onPageChange={setPagination}
          >
            {getSelectionColumn({
              total: pagination.total,
              selected: selectedEntities.length,
              isLoading: checkboxIsLoading,
              onToggleAll: onToggleAllHandler,
            })}

            {getEntityIdColumn()}
            <Column body={getPreviewBodyTemplate} className="center w200" resizeable={false} />
            <Column body={getDescriptionBodyTemplate} resizeable={false} />
          </TableWithPagination>

          <TotalSelectedLabel selectedCount={checkboxIsLoading ? Number(pagination.total) : selectedEntities.length} />
        </TableWrapper>
      </DndDropzone>
    </Loading>
  );
};
